/**
 * Public facade for the CFO forecast Excel export.
 *
 * The export is composed as a rich multi-sheet SpreadsheetWorkbookSnapshot
 * (see cfoForecastWorkbookModel.ts) and emitted through FuseGrid's in-house
 * OOXML engine — which supports live formulas, data-validation dropdowns,
 * conditional formatting, named ranges, protection and (Phase 2) native charts.
 * The previous SheetJS path is retired.
 */
import { buildSpreadsheetExcelOoxmlPackageFromWorkbookSnapshot } from '../../../protocol/spreadsheetExcelOoxmlPackage'
import {
  buildCfoForecastWorkbookSnapshot,
  type CfoForecastWorkbookInput,
} from './cfoForecastWorkbookModel'

export type { CfoForecastWorkbookInput } from './cfoForecastWorkbookModel'
export { buildCfoForecastWorkbookSnapshot } from './cfoForecastWorkbookModel'

const safeExcelSegment = (raw: string): string => (
  String(raw || '')
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 90)
)

/**
 * Filename for the full workbook. The workbook now contains all three statements
 * and all three cases, so there is no per-statement / per-case suffix.
 */
export function buildCfoForecastExcelFileName(args: {
  companyName?: string | null
  anchorPeriod?: string | null
  grain?: string | null
}): string {
  const company = safeExcelSegment(args.companyName || 'CFO Core')
  const grain = safeExcelSegment(args.grain || '')
  const anchor = safeExcelSegment(args.anchorPeriod || new Date().toISOString().slice(0, 10))
  const base = safeExcelSegment([company, 'Cash-Flow Forecast', grain, anchor].filter(Boolean).join(' '))
  return `${base || 'CFO Core Cash-Flow Forecast'}.xlsx`
}

/** Build the workbook snapshot and emit `.xlsx` bytes through the in-house engine. */
export function writeCfoForecastWorkbookExcelBytes(input: CfoForecastWorkbookInput): {
  bytes: Uint8Array
  warnings: string[]
} {
  const { snapshot } = buildCfoForecastWorkbookSnapshot(input)
  const exported = buildSpreadsheetExcelOoxmlPackageFromWorkbookSnapshot({ snapshot })
  return {
    bytes: exported.package_bytes,
    warnings: exported.warnings.map((w) => w.message),
  }
}
